import React, { ReactElement, useEffect, useState, useMemo } from 'react';
import { Flex } from 'rebass/styled-components';
import {
  useRouteMatch,
  useHistory,
  Route,
  Switch,
  useParams,
  Redirect,
} from 'react-router-dom';

import AccountInformation from './AccountInformation';
import OfficeFacts from './OfficeFacts';
import LocationList from './LocationList';
import ContactList from './ContactList';
import CustomTables from './CustomTables';
import FTPSettings from './FTPSettings';
import XMLTagMapping from './XMLTagMapping';
import * as Icon from '../../assets/icons';
import ImportAccount from '../ImportAccount';
import ProcedureBuilder from '../ProcedureBuilder/index';
import FormList from '../FormsList/index';
import AddEditForm from 'containers/AddEditForm';
import Telephony from './Telephony';

import { MainContent, PageHeader, PageContent, VerticalNav } from 'ui/patterns';

import { useDocumentTitle, useGetAccountInfo } from 'hooks';

import { confirmAlert } from 'utils/confirm-alert';

const tabs = [
  {
    id: 'accountInfo',
    label: 'Account Info',
    icon: <Icon.LgBriefcase />,
  },
  {
    id: 'officeFacts',
    label: 'Office Facts',
    icon: <Icon.LgClipboardCheck />,
  },
  {
    id: 'locationList',
    label: 'Location List',
    icon: <Icon.LgLocation />,
  },
  { id: 'contactList', label: 'Contact List', icon: <Icon.LgCall /> },
  { id: 'customTables', label: 'Custom Tables', icon: <Icon.LgTable /> },
  { id: 'XMLTagMapping', label: 'XML Tag Mapping', icon: <Icon.LgTag /> },
  {
    id: 'FTPSettings',
    label: 'FTP Settings',
    groupEnd: true,
    icon: <Icon.LgServer />,
  },
  { id: 'FormList', label: 'Form List', icon: <Icon.LgList /> },
  { id: 'Procedures', label: 'Procedures', icon: <Icon.LgDocument /> },
  ...(process.env.REACT_APP_THEME?.toLocaleLowerCase() === 'protocall'
    ? [
        {
          id: 'Telephony',
          label: 'Telephony',
          icon: <Icon.LgCall />,
        },
      ]
    : []),
];

function AddEditAccount(): ReactElement {
  const [showImportAccount, setImportAccount] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [formId, setFormId] = useState<number>();
  const [collapseSidenav, setCollapseSidenav] = useState(false);
  const permissions = JSON.parse(
    localStorage.getItem('userPermissions') || '{}',
  );

  const { accountId: passedId, tabId } = useParams<{
    accountId: string;
    tabId?: string;
  }>();
  const accountId = parseInt(passedId);

  const { data, isLoading } = useGetAccountInfo(accountId);

  const match = useRouteMatch('/home/accounts/add');
  const history = useHistory<{ ID: number; tabId?: string }>();
  const accountName = isLoading
    ? 'Processing'
    : `${data?.accountInfoReturn.Data.Name} `;
  const importAccountName = isLoading
    ? 'Processing'
    : `${data?.accountInfoReturn.Data.AccountNameAndNumber} `;

  const subtitle = () => {
    if (tabId === 'accountInfo') return 'Edit Account';
    if (tabId === 'AddForm ') return 'Add Form';
    if (tabId === 'EditForm') return 'Edit Form';
    if (tabId === undefined) return 'Add Account';
  };

  useDocumentTitle(subtitle(), true);

  const pageTitle = `${match ? 'Create New Account' : accountName}`;
  const pageSubTitle =
    !isLoading && !match
      ? `${data?.accountInfoReturn.Data.CID}${'-'}${
          data?.accountInfoReturn.Data.SID
        }${'-'}${data?.accountInfoReturn.Data.PID}`
      : null;

  useEffect(() => {
    if (
      tabId === 'Procedures' ||
      tabId === 'FormList' ||
      tabId === 'AddForm' ||
      tabId === 'EditForm'
    ) {
      setCollapseSidenav(true);
    } else if (tabId !== 'EditForm') {
      setFormId(undefined);
    }
  }, [tabId]);

  const accountData = useMemo(() => {
    if (data?.accountInfoReturn?.Data) {
      return {
        Id: data.accountInfoReturn.Data.Id,
        CID: data.accountInfoReturn.Data.CID,
        SID: data.accountInfoReturn.Data.SID,
        PID: data.accountInfoReturn.Data.PID,
        StatusId: data.accountInfoReturn.Data.StatusId,
        AccountServiceId: data.accountInfoReturn.Data.AccountServiceId,
        Name: data.accountInfoReturn.Data.Name,
        AnswerScript: data.accountInfoReturn.Data.AnswerScript,
      };
    }
  }, [data?.accountInfoReturn.Data]);

  const toggleImportAccount = () => {
    setImportAccount(false);
  };

  let pageActions;
  if (!match) {
    pageActions = [
      {
        action: 'Import Account Data',
        icon: <Icon.Import ariaHidden="true" />,
        handleButtonClick: () => {
          setImportAccount(true);
        },
      },
    ];
  }

  const onNavClick = (tabId: string) => {
    const nextPath = `/home/accounts/edit/${accountId}/${tabId}`;
    if (isDirty) {
      confirmAlert({
        message: 'Changes you made may not be saved.',
        buttons: [
          {
            label: 'Leave',
            onClick: () => {
              history.push(nextPath);
              setDirty(false);
            },
            className: 'pr-btn-primary',
          },
          {
            label: 'Cancel',
            onClick: () => {
              //
            },
          },
        ],
      });
    } else history.push(nextPath);
  };

  const toggleCollapseSidenav = () => setCollapseSidenav(!collapseSidenav);

  return (
    <>
      <MainContent flexDirection="row">
        {!match ? (
          <VerticalNav
            tabs={tabs}
            activeTab={tabId || ''}
            onNavClick={onNavClick}
            collapsed={collapseSidenav}
            toggleCollapse={toggleCollapseSidenav}
          />
        ) : null}

        <Flex flexGrow={1} flexDirection="column">
          {tabId === 'Procedures' ||
          tabId === 'FormList' ||
          tabId === 'AddForm' ||
          tabId === 'EditForm' ||
          tabId === 'Telephony' ? null : (
            <PageHeader
              title={pageTitle}
              subTitle={pageSubTitle}
              proteusAccountId={accountData?.Id}
              actions={pageActions}
              tabId={tabId}
            />
          )}
          <Switch>
            <PageContent>
              <Route
                path={`/home/accounts/add`}
                component={AccountInformation}
                exact
              />
              <Route
                path={`/home/accounts/edit/:accountId/accountInfo`}
                component={AccountInformation}
                exact
              />
              <Route
                path={`/home/accounts/edit/:accountId/officeFacts`}
                component={OfficeFacts}
                exact
              />
              <Route
                path={`/home/accounts/edit/:accountId/locationList`}
                component={LocationList}
                exact
              />
              <Route
                path={`/home/accounts/edit/:accountId/contactList`}
                component={ContactList}
                exact
              />
              <Route
                path={`/home/accounts/edit/:accountId/customTables`}
                component={CustomTables}
                exact
              />
              <Route
                path={`/home/accounts/edit/:accountId/XMLTagMapping`}
                component={XMLTagMapping}
                exact
              />
              <Route
                path={`/home/accounts/edit/:accountId/FTPSettings`}
                component={FTPSettings}
                exact
              />
              <Route
                path={`/home/accounts/edit/:accountId/Procedures`}
                component={ProcedureBuilder}
                exact
              />
              <Route
                path={`/home/accounts/edit/:accountId/Telephony`}
                render={() => {
                  return process.env.REACT_APP_THEME?.toLocaleLowerCase() ===
                    'protocall' ? (
                    <Telephony />
                  ) : (
                    <Redirect to="/home" />
                  );
                }}
                exact
              />

              <Route
                path={`/home/accounts/edit/:accountId/FormList`}
                component={FormList}
                exact
              />
              <Route
                path={`/home/accounts/edit/:accountId/AddForm`}
                component={AddEditForm}
                exact
              />
              <Route
                path={`/home/accounts/edit/:accountId/EditForm/:formId`}
                component={AddEditForm}
                exact
              />
            </PageContent>
          </Switch>
        </Flex>
      </MainContent>
      {showImportAccount && (
        <ImportAccount
          showPanel={showImportAccount}
          togglePanel={toggleImportAccount}
          accountName={importAccountName}
          importAccountId={accountId || 0}
          account={accountData ? accountData : undefined}
        />
      )}
    </>
  );
}

export default AddEditAccount;
